/**
 * Pure computation functions for the Time-Trial Fitness Assessment feature.
 *
 * These functions implement zone classification, threshold derivation,
 * deduplication, formatting, color mapping, recommendation adjustment,
 * and cross-group upgrade logic.
 *
 * Requirements: 1.2, 1.3, 1.4, 1.5, 1.8, 1.9, 2.1, 2.2, 2.3, 2.4,
 *               3.5, 3.6, 5.2, 5.3, 5.4, 5.5, 5.6, 6.3, 6.4, 6.5
 */

/**
 * Zone classification for a rider's time-trial performance relative to their group.
 */
export type TimeTrialZone =
  | 'Behind Group'
  | 'With Group'
  | 'Ahead of Group'
  | 'Ahead of Group+'
  | 'No Data'
  | 'Insufficient Group Data';

/** ±10% band around the group median for zone boundaries. */
export const ZONE_BAND_PERCENT = 10;

/** Rider must beat ≥25% of the higher group to earn "Ahead of Group+". */
export const CROSS_GROUP_THRESHOLD_PERCENT = 25;

/**
 * Classify a rider into "Behind Group", "With Group", or "Ahead of Group"
 * using a ±10% band around the group median.
 *
 * Lower time = faster = better performance.
 * - rider_time >= median × 1.10 → "Behind Group"
 * - rider_time <= median × 0.90 → "Ahead of Group"
 * - otherwise → "With Group"
 *
 * Requirements: 1.3, 1.4, 1.5, 1.9
 */
export function computeZone(riderTime: number, groupMedian: number): string {
  const { ahead, behind } = computeThresholds(groupMedian);

  if (riderTime >= behind) return 'Behind Group';
  if (riderTime <= ahead) return 'Ahead of Group';
  return 'With Group';
}

/**
 * Derive ahead and behind thresholds from the group median.
 *
 * ahead  = median × 0.90 (faster boundary)
 * behind = median × 1.10 (slower boundary)
 *
 * Requirements: 1.2, 2.1
 */
export function computeThresholds(median: number): { ahead: number; behind: number } {
  const ahead = median * (1 - ZONE_BAND_PERCENT / 100);
  const behind = median * (1 + ZONE_BAND_PERCENT / 100);
  return { ahead, behind };
}

/**
 * Keep only the latest record per rider_id (deduplicate by timestamp).
 *
 * Requirements: 1.8, 2.3
 */
export function deduplicateTrials(
  trials: { rider_id: string; timestamp: string; recorded_time: number }[]
): { rider_id: string; timestamp: string; recorded_time: number }[] {
  const latest = new Map<string, { rider_id: string; timestamp: string; recorded_time: number }>();

  for (const trial of trials) {
    const existing = latest.get(trial.rider_id);
    if (!existing || trial.timestamp.localeCompare(existing.timestamp) > 0) {
      latest.set(trial.rider_id, trial);
    }
  }

  return Array.from(latest.values());
}

/**
 * Normalize a direction string to lowercase for consistent filtering.
 *
 * Requirements: 2.4
 */
export function normalizeDirection(direction: string): string {
  return direction.toLowerCase();
}

/**
 * Format seconds as MM:SS with zero-padded seconds.
 *
 * Requirements: 3.5, 3.6
 */
export function formatTimeTrialTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Map a TimeTrialZone to its display color.
 *
 * - "Behind Group"            → orange
 * - "With Group"              → blue
 * - "Ahead of Group"          → green
 * - "Ahead of Group+"         → dark green
 * - "No Data"                 → gray
 * - "Insufficient Group Data" → gray
 *
 * Requirements: 3.2, 3.3, 3.4
 */
export function getZoneColor(zone: TimeTrialZone): string {
  switch (zone) {
    case 'Behind Group':
      return 'orange';
    case 'With Group':
      return 'blue';
    case 'Ahead of Group':
      return 'green';
    case 'Ahead of Group+':
      return 'dark green';
    case 'No Data':
    case 'Insufficient Group Data':
      return 'gray';
  }
}

/**
 * Apply time-trial zone adjustment to a base recommendation.
 *
 * Adjustment rules:
 * - "Ahead of Group"  + "On track"                          → "Ready to advance"
 * - "Ahead of Group+" + any (except "Insufficient assessments") → "Ready to advance"
 * - "Behind Group"    + "Ready to advance"                  → "On track"
 * - "With Group", "No Data", "Insufficient Group Data"      → no change
 *
 * Requirements: 5.2, 5.3, 5.4, 5.5, 5.6
 */
export function applyTimeTrialAdjustment(
  baseSuggestion: string,
  zone: TimeTrialZone
): { suggestion: string; adjustment?: string } {
  if (zone === 'Ahead of Group' && baseSuggestion === 'On track') {
    return {
      suggestion: 'Ready to advance',
      adjustment: 'Adjusted by time-trial performance: Ahead of Group',
    };
  }

  if (zone === 'Ahead of Group+' && baseSuggestion !== 'Insufficient assessments') {
    return {
      suggestion: 'Ready to advance',
      adjustment: 'Adjusted by time-trial performance: Ahead of Group+',
    };
  }

  if (zone === 'Behind Group' && baseSuggestion === 'Ready to advance') {
    return {
      suggestion: 'On track',
      adjustment: 'Adjusted by time-trial performance: Behind Group',
    };
  }

  // "With Group", "No Data", "Insufficient Group Data" → no change
  return { suggestion: baseSuggestion };
}

/**
 * Determine if a rider qualifies for "Ahead of Group+" by beating ≥25%
 * of the higher group's riders.
 *
 * Uses floor calculation for the threshold count, and threshold_count must be > 0.
 *
 * Requirements: 6.3, 6.4, 6.5
 */
export function computeCrossGroupUpgrade(
  riderTime: number,
  higherGroupTimes: number[]
): { upgraded: boolean; ridersBeaten: number } {
  if (higherGroupTimes.length === 0) {
    return { upgraded: false, ridersBeaten: 0 };
  }

  const ridersBeaten = higherGroupTimes.filter((t) => riderTime < t).length;
  const thresholdCount = Math.floor(
    higherGroupTimes.length * CROSS_GROUP_THRESHOLD_PERCENT / 100
  );

  const upgraded = thresholdCount > 0 && ridersBeaten >= thresholdCount;
  return { upgraded, ridersBeaten };
}
